import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Sparkles, CheckCircle2, MessageCircle } from 'lucide-react';
import { API, submitEnquiry, resolveImg } from '../api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PackageCarousel from '../components/PackageCarousel';
import SafeImage from '../components/SafeImage';
import useAntiSpam from '../hooks/useAntiSpam';
import { applyPageSEO } from '../utils/pageSEO';

const LandingEnquiryForm = ({ source, utm, sticky = false }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const { fields: antiSpamFields, honeypotInput, readHoneypot } = useAntiSpam();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { setErr('Please share your name and phone.'); return; }
    setErr(''); setLoading(true);
    try {
      await submitEnquiry({
        ...form,
        source,                       // slug of the landing page — used by admin to trace campaigns
        ...(utm || {}),               // utm_source, utm_medium, utm_campaign (only when present)
        ...antiSpamFields,
        website: readHoneypot(),
      });
      setSent(true);
      setForm({ name: '', phone: '', email: '', message: '' });
    } catch (_) {
      setErr('Could not send. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={sticky ? 'landing-enquiry-form-sticky' : 'landing-enquiry-form'}
      className="bg-white border border-gray-200 rounded-xl shadow-lg p-5 space-y-2.5"
    >
      <div className="flex items-center gap-2 pb-1">
        <Sparkles size={16} className="text-[#ffcc29]" />
        <h3 className="text-base font-bold text-gray-900">Get a Custom Quote</h3>
      </div>
      <p className="text-xs text-gray-500 -mt-1 mb-1">
        A travel expert calls back within a working day. No spam, promise.
      </p>
      {sent ? (
        <div className="text-center py-6" data-testid="landing-enquiry-success">
          <CheckCircle2 className="mx-auto text-green-500 mb-2" size={32} />
          <p className="text-sm font-medium text-gray-800">Thanks! We will call you back shortly.</p>
          <button
            type="button"
            onClick={() => setSent(false)}
            className="text-xs text-[#0d64a8] mt-2 underline"
          >
            Send another enquiry
          </button>
        </div>
      ) : (
        <>
          {honeypotInput}
          <input
            data-testid="landing-enquiry-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name*"
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]"
          />
          <input
            data-testid="landing-enquiry-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone number*"
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]"
          />
          <input
            data-testid="landing-enquiry-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email (optional)"
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]"
          />
          <textarea
            data-testid="landing-enquiry-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Tell us your dates, group size or preferences"
            rows={3}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8] resize-none"
          />
          {err && <p className="text-xs text-red-500">{err}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="landing-enquiry-submit"
            className="w-full bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm font-semibold py-2.5 rounded-md flex items-center justify-center gap-1.5 disabled:opacity-70"
          >
            <Send size={14} /> {loading ? 'Sending...' : 'Send Enquiry'}
          </button>
          <p className="text-[10px] text-gray-400 text-center pt-1">
            Tagged as <span className="font-medium text-gray-600">/{source}</span> so our team knows exactly which campaign you came from.
          </p>
        </>
      )}
    </form>
  );
};

const LandingPageView = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const formRef = useRef(null);

  // Pull UTM params from URL for paid-ad tracking. Empty object if none present.
  const utm = React.useMemo(() => {
    const out = {};
    ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => {
      const v = searchParams.get(k);
      if (v) out[k] = v.slice(0, 120); // trim absurdly long values
    });
    return out;
  }, [searchParams]);

  useEffect(() => {
    setData(null); setNotFound(false);
    axios.get(`${API}/landing-pages/${slug}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true));
    // Fire-and-forget view tracking; ignore errors silently.
    axios.post(`${API}/landing-pages/${slug}/view`).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!data?.page) return;
    const p = data.page;
    const desc = p.seo_description || p.subtitle || (p.description || '').substring(0, 160);
    return applyPageSEO({
      title: p.seo_title || p.title,
      description: desc,
      noIndex: !!p.seo_no_index,
      noFollow: !!p.seo_no_follow,
    });
  }, [data]);

  const scrollToEnquiry = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-[820px] mx-auto px-5 py-24 text-center">
          <h1 className="text-2xl font-bold text-gray-800">Landing page not found</h1>
          <p className="text-gray-500 mt-2 text-sm">This campaign has ended or the link is incorrect.</p>
          <Link to="/" className="inline-block mt-6 bg-[#0d64a8] text-white text-sm px-5 py-2 rounded-md">Back to home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-[820px] mx-auto px-5 py-24 text-center text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  const page = data.page;
  const packages = data.packages || [];
  const heroImg = resolveImg(page.hero_image);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-view">
      <Header />

      {/* HERO */}
      <section className="relative h-[520px] md:h-[600px] overflow-hidden">
        <SafeImage
          src={page.hero_image || ''}
          alt={page.title}
          fallbackType="beach"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/45 to-black/70" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-[1200px] mx-auto px-5 lg:px-8 w-full text-white">
            <Link to="/" className="inline-flex items-center gap-1.5 text-white/85 hover:text-white text-sm mb-4">
              <ArrowLeft size={14} /> Back to home
            </Link>
            <div className="max-w-[720px]">
              <span className="inline-block bg-[#ffcc29] text-[#0a2a4a] text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
                Featured Campaign
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight drop-shadow-lg" data-testid="landing-hero-title">
                {page.title}
              </h1>
              {page.subtitle && (
                <p className="mt-4 text-lg md:text-xl text-white/90 leading-relaxed max-w-[640px]" data-testid="landing-hero-subtitle">
                  {page.subtitle}
                </p>
              )}
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  onClick={scrollToEnquiry}
                  data-testid="landing-hero-cta"
                  className="bg-[#ffcc29] hover:bg-[#e6b820] text-[#0a2a4a] font-semibold text-sm px-6 py-3 rounded-md shadow-lg inline-flex items-center gap-2"
                >
                  <Send size={14} /> Send Enquiry
                </button>
                <a
                  href="#packages"
                  className="bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 text-white font-semibold text-sm px-6 py-3 rounded-md inline-flex items-center gap-2"
                >
                  Explore Packages ({packages.length})
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main content — two columns on desktop with sticky form */}
      <section className="py-12 md:py-16">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left: description */}
          <div className="lg:col-span-2">
            <div className="prose prose-lg max-w-none text-gray-800">
              {(page.description || '').split(/\n{2,}/).map((para, i) => (
                <p key={i} className="mb-5 leading-relaxed text-[16.5px]">{para}</p>
              ))}
            </div>

            {/* Packages grid */}
            <div id="packages" className="mt-10 pt-6 border-t border-gray-100">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-testid="landing-packages-heading">
                Curated Packages
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {packages.length > 0 ? `${packages.length} handpicked option${packages.length > 1 ? 's' : ''} for this campaign.` : 'Packages will be added shortly.'}
              </p>
              {packages.length > 0 ? (
                <PackageCarousel items={packages} />
              ) : (
                <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                  No packages linked to this landing page yet.
                </div>
              )}
            </div>
          </div>

          {/* Right: sticky enquiry form (desktop) */}
          <div ref={formRef} className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <LandingEnquiryForm source={page.slug} utm={utm} sticky />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile floating CTA */}
      <div className="lg:hidden fixed bottom-4 inset-x-4 z-30">
        <button
          onClick={scrollToEnquiry}
          data-testid="landing-mobile-cta"
          className="w-full bg-[#0d64a8] text-white text-sm font-semibold py-3 rounded-md shadow-lg flex items-center justify-center gap-2"
        >
          <MessageCircle size={16} /> Send Enquiry
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPageView;
